import { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';

import { navGroups } from './navItems';

type Crumb = { label: string; href?: string };

/**
 * Простой breadcrumbs: пытается восстановить цепочку по navItems.
 * Если страница нестандартная (например /estimation/123) — берёт label
 * из ближайшего навигационного предка + хвост сегмента.
 */
const Breadcrumbs = () => {
  const { pathname } = useLocation();

  const crumbs = useMemo<Crumb[]>(() => {
    const result: Crumb[] = [];
    // Найдём первый совпадающий nav item
    for (const group of navGroups) {
      for (const item of group.items) {
        if (pathname === item.path || (item.path !== '/' && pathname.startsWith(item.path))) {
          result.push({ label: group.label });
          result.push({ label: item.label, href: item.path });
          // Хвост (детальная страница, create, edit/:id)
          const tail = pathname.slice(item.path.length).split('/').filter(Boolean);
          if (tail.length > 0) {
            const last = tail[tail.length - 1];
            const SEGMENT_LABELS: Record<string, string> = {
              create: 'Создание',
              edit: 'Редактирование',
              prompts: 'Системные промпты',
            };
            const friendly = SEGMENT_LABELS[last] ?? decodeURIComponent(last);
            result.push({ label: friendly });
          }
          return result;
        }
      }
    }
    return result;
  }, [pathname]);

  if (crumbs.length === 0) return null;

  return (
    <div
      style={{
        height: 'var(--shell-breadcrumbs-h)',
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        padding: '0 20px',
        borderBottom: '1px solid var(--shell-border-subtle)',
        background: 'var(--shell-bg-canvas)',
        fontSize: 12,
        color: 'var(--shell-text-tertiary)',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
      }}
    >
      {crumbs.map((c, i) => {
        const isLast = i === crumbs.length - 1;
        return (
          <span key={`${c.label}-${i}`} style={{ display: 'inline-flex', alignItems: 'center' }}>
            {i > 0 && (
              <ChevronRight
                size={12}
                strokeWidth={1.75}
                color="var(--shell-text-tertiary)"
                style={{ margin: '0 4px' }}
              />
            )}
            {c.href && !isLast ? (
              <Link
                to={c.href}
                style={{
                  color: 'var(--shell-text-secondary)',
                  textDecoration: 'none',
                }}
              >
                {c.label}
              </Link>
            ) : (
              <span
                style={{
                  color: isLast ? 'var(--shell-text-primary)' : 'var(--shell-text-secondary)',
                  fontWeight: isLast ? 500 : 400,
                }}
              >
                {c.label}
              </span>
            )}
          </span>
        );
      })}
    </div>
  );
};

export default Breadcrumbs;
